import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Button, Modal } from "@mui/material";
import { Button as Btn, Typography } from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import Project from "../../model/project";
import ProjectModal from "./ProjectModal";
import DateConverter from "../../utils/DateConverter";
import DB_Status from "../../model/enum/status.class";
import Avatar from "../../shared/components/images/Avatar";
import BundleItem from "../../model/bundle-item";
import ProjectImagesModal from "./ProjectImagesModal";
import EstimateDetailModal from "../estimate/EstimateDetailModal";
import useStyles from "./styles";
import ShareModal from "./ShareModal";
import UpdateStatusModal from "./UpdateStatusModal";
import ApplyModal from "./ApplyModal";
import ProfileIcon from "../../assets/admin/profile-icon.svg";
import CalendarIcon from "../../assets/admin/calendar-icon.svg";
import TimeIcon from "../../assets/admin/time-icon.svg";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "70rem",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "88%",
  },
};

interface Props {
  project: Project;
  resetList: () => void;
  isRow: boolean;
  onOpen?: (id?: string) => void;
  onClose?: () => void;
}

const ProjectDetailModal = ({
  project,
  resetList,
  isRow,
  onOpen,
  onClose,
}: Props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const projectUrl = `${new URL(window.location.href)}`;

  const handleOpen = () => {
    setOpen(true);
    if (onOpen) {
      onOpen(project.id?.toString());
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    const projectId = searchParams.get("projectId");
    if (!open && projectId && projectId === project.id?.toString()) {
      handleOpen();
    }
  }, [searchParams]);

  return (
    <div>
      {project.assignedTo == null ? (
        <div className={classes.itemGrid} key={project.id} onClick={handleOpen}>
          <div className={classes.itemHeader}>
            <Avatar name={project.createdBy} />
            <Typography className={classes.priceText}>
              ${project.rate}
            </Typography>
          </div>
          <div className={classes.itemBody}>
            <Typography className={classes.title}>{project.name}</Typography>
            <Typography
              className={classes.description}
              style={{ maxWidth: isRow ? "450px" : "1200px" }}
            >
              {project.description}
            </Typography>
          </div>
          <div className="d-flex col mt-2 align-items-center">
            <div className="d-flex col">
              <img src={CalendarIcon} className={classes.icon} alt="calendar" />
              <Typography className={classes.time}>
                {DateConverter.toMonthNameDateAndYear(project.startDate)}
              </Typography>
            </div>
            {project.User ? (
              <Avatar name={project.User?.firstName} />
            ) : (
              <img src={ProfileIcon} className={classes.img} alt="profile" />
            )}
          </div>
          <div className="d-flex col align-items-center">
            <img src={TimeIcon} className={classes.icon} alt="time" />
            <Typography className={classes.time}>
              {DateConverter.toHoursAndMinutes(project.createdAt)}
            </Typography>
          </div>
          <div
            className={classes.buttonDiv}
            style={{ marginTop: isRow ? "5%" : "0%" }}
          >
            <Btn className={classes.button} onClick={handleOpen}>
              Apply now
            </Btn>
          </div>
        </div>
      ) : (
        <div className={classes.takenDiv} key={project.id}>
          <div className={classes.takenSubDiv}>
            <Typography className={classes.takenTitle}>Taken by</Typography>
            <Avatar
              name={`${project.User?.firstName} ${project.User?.lastName}`}
              size={65}
              fontSize={20}
            />
            <div className="d-flex col align-items-center">
              <div className="d-flex row">
                <img src={TimeIcon} className={classes.icon} alt="time" />
                <Typography
                  className={classes.time}
                  style={{ marginTop: "10px" }}
                >
                  {DateConverter.toMonthNameDateAndYear(project.startDate)}
                </Typography>
                <Typography className={classes.time}>
                  {DateConverter.toHoursAndMinutes(project.createdAt)}
                </Typography>
              </div>
            </div>
          </div>
          <div
            className={classes.buttonDiv}
            style={{ marginTop: isRow ? "5%" : "0%" }}
          >
            <Btn className={classes.button} onClick={handleOpen}>
              Show details
            </Btn>
          </div>
        </div>
      )}
      {open && (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          sx={{ overflowY: "auto" }}
        >
          <Box sx={style}>
            <div className={classes.headerDiv}>
              <div className={classes.headerSubDiv2}>
                <Typography className={classes.title}>Project</Typography>
                <div
                  className="vl"
                  style={{ marginLeft: "1rem", marginRight: "1rem" }}
                ></div>
                <Typography className={classes.subtitle}>
                  {project.code}
                </Typography>
                <div
                  className={classes.headerSubDiv2}
                  style={{ marginLeft: "1rem" }}
                >
                  <ShareModal url={projectUrl} />
                  <ProjectModal
                    project={project}
                    resetList={resetList}
                    isRow={isRow}
                  />
                  <ProjectImagesModal project={project} />
                </div>
              </div>
              <div className={classes.headerSubDiv3}>
                {project.User == null && (
                  <ApplyModal project={project} resetList={resetList} />
                )}
                <UpdateStatusModal project={project} resetList={resetList} />
                <Btn onClick={handleClose} style={{ color: "black" }}>
                  <CloseIcon />
                </Btn>
              </div>
            </div>
            <hr />
            <div className={classes.form}>
              <div className={classes.detailsDiv}>
                <div className={classes.rightDiv}>
                  <Typography className={classes.title}>
                    Project overview
                  </Typography>
                  <div className={classes.textDiv}>
                    <label className={classes.label2}>Name</label>
                    <Typography className={classes.text}>
                      {project.name}
                    </Typography>
                  </div>
                  <div className={classes.textDiv}>
                    <label className={classes.label2}>Address</label>
                    <Typography className={classes.text}>
                      {project.Client?.street}, {project.Client?.city} -{" "}
                      {project.Client?.state}
                    </Typography>
                  </div>
                  <div className={classes.textDiv}>
                    <label className={classes.label2}>Rate</label>
                    <Typography className={classes.text}>
                      {project.rate ? `$${project.rate}.00` : "Not specified"}
                    </Typography>
                  </div>
                  <div className={classes.textDiv}>
                    <label className={classes.label2}>Project type</label>
                    <Typography className={classes.text}>
                      {project.ProjectType?.name}
                    </Typography>
                  </div>
                  <div className={classes.textDiv}>
                    <label className={classes.label2}>Team</label>
                    <Typography className={classes.text}>
                      {project.Team?.name}
                    </Typography>
                  </div>
                  <div className={classes.textDiv}>
                    <label className={classes.label2}>Contractor</label>
                    <Typography className={classes.text}>
                      {project.Contractor
                        ? `${project.Contractor?.firstName} ${project.Contractor?.lastName}`
                        : "-"}
                    </Typography>
                  </div>
                  <div className={classes.textDiv}>
                    <label className={classes.label2}>Status</label>
                    <div
                      style={{
                        width: 150,
                        height: "auto",
                      }}
                    >
                      <Typography
                        style={DB_Status.mapStatusColor(project.Status?.name)}
                      >
                        <div
                          style={DB_Status.mapStatusRoundedColor(
                            project.Status?.name
                          )}
                        ></div>
                        {project.Status?.name}
                      </Typography>
                    </div>
                  </div>
                  <div className={classes.textDiv}>
                    <label className={classes.label2}>Start date</label>
                    <Typography className={classes.text}>
                      {DateConverter.toMonthNameDateAndYear(project.startDate)}
                    </Typography>
                  </div>
                  <div className={classes.textDiv}>
                    <label className={classes.label2}>Due date</label>
                    <Typography className={classes.text}>
                      {DateConverter.toMonthNameDateAndYear(project.dueDate)}
                    </Typography>
                  </div>
                  <div className={classes.textDiv}>
                    <label className={classes.label2}>Created by</label>
                    <Avatar name={project.createdBy} size={25} fontSize={12} />
                    <Typography
                      className={classes.text}
                      style={{ marginLeft: 7 }}
                    >
                      {project.createdBy}
                      <span
                        style={{
                          fontSize: 12,
                          marginLeft: 10,
                          color: "#A0A0A0",
                        }}
                      >
                        {DateConverter.toDaysAgoFormat(project.createdAt)}
                      </span>
                    </Typography>
                  </div>
                  <div className={classes.textDiv}>
                    <label className={classes.label2}>Description</label>
                    <Typography
                      className={classes.text}
                      style={{ fontWeight: 400 }}
                    >
                      {project.description}
                    </Typography>
                  </div>
                </div>
                <div className={classes.leftDiv}>
                  <div className="d-flex col align-items-center">
                    <Typography className={classes.title}>
                      Project breakdown
                    </Typography>
                    {project.Estimate && (
                      <EstimateDetailModal
                        estimate={project.Estimate}
                        externalButton={true}
                      />
                    )}
                  </div>
                  <table className={classes.table}>
                    <thead>
                      <tr>
                        <th className={classes.tableHeader}>#</th>
                        <th className={classes.tableHeader}>Product/Service</th>
                        <th className={classes.tableHeader}>Description</th>
                        <th className={classes.tableHeader}>Quantity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {project.Estimate?.items?.map((item) => (
                        <React.Fragment key={item.id}>
                          {/* Parent Row */}
                          <tr key={item.id}>
                            <td>{item.id}</td>
                            <td>{item.task}</td>
                            <td>{item.description}</td>
                            <td>{item.quantity}</td>
                          </tr>
                          {/* Child Rows */}
                          <>
                            {item?.BundleItems?.map((child: BundleItem) => (
                              <tr
                                key={child.id}
                                style={{
                                  paddingLeft: "20px",
                                  color: "#757575",
                                }}
                              >
                                <td></td>
                                <td>{child?.Product?.name}</td>
                                <td>{child?.Product?.description}</td>
                                <td>{child.quantity}</td>
                              </tr>
                            ))}
                          </>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </Box>
        </Modal>
      )}
    </div>
  );
};

export default ProjectDetailModal;
